/**
 * StatusRowStack - Unified status row container for the compact card.
 *
 * Replaces ContractStatusRow + StatusPills + CardAlertBanner with a single
 * vertical stack of color-coded status rows. Hosts action dialogs for
 * lease renewal and final departure (migrated from ContractStatusRow).
 */

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { StatusRow, type StatusRowDropdownItem } from './StatusRow'
import { computeStatusRows, type StatusRowDef } from '@/lib/utils/compute-status-rows'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { useFinalDepartureWithLeaseComplete, useCheckOutEvent, useCheckInEvent } from '@/hooks/useAllocationEvents'
import { useUpcomingAircraftSchedules } from '@/hooks/useAircraftSchedules'
import { useTerminateLease } from '@/hooks/useLeases'
import { useCancelTransientStay, useCheckOutTransientStay, useNoShowTransientStay } from '@/hooks/useTransientStays'
import { LeaseMutateDrawer, type LeasePrefillData } from '@/components/leases/LeaseMutateDrawer'
import { TerminateLeaseDialog } from '@/components/leases/TerminateLeaseDialog'
import {
  TransientStayMutateDrawer,
  CancelTransientStayDialog,
  NoShowTransientStayDialog,
  CheckOutTransientStayDialog,
} from '@/components/transient-stays'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import type { AllocationEnrichedData, StatusRowSeverity } from '@/types/aircraft-card'
import { inferFuelTypeFromCategory } from '@/types/aircraft-types'

const MAX_VISIBLE = 5

/** Map actionType strings to human-readable button labels */
const ACTION_LABELS: Record<string, string> = {
  renew_lease: 'Renew',
  edit_insurance: 'Update',
  checkout_allocation: 'Check Out',
  checkout_transient: 'Check Out',
  checkin_transient: 'Check In',
  no_show_transient: 'No Show',
  mark_returned: 'Return',
  log_fuel: 'Log Fuel',
  add_departure_schedule: 'Add Schedule',
}

interface StatusRowStackProps {
  data: AllocationEnrichedData
  /** When set, only show rows matching these severities (e.g. ['critical', 'warning']) */
  filterSeverities?: StatusRowSeverity[]
  /** Opens the unified LeaseDetailPanel when a lease row is clicked */
  onLeaseDetailsClick?: () => void
  /** Opens the TransientStayDetailPanel when a transient row is clicked */
  onTransientDetailsClick?: () => void
}

export function StatusRowStack({ data, filterSeverities, onLeaseDetailsClick, onTransientDetailsClick }: StatusRowStackProps) {
  const openFullModal = useAircraftCardStore((s) => s.openFullModal)
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)
  const closeCard = useAircraftCardStore((s) => s.closeCard)
  const openCompleteSetup = useAddPlaneWizardStore((s) => s.openCompleteSetup)
  const { locationId } = useAppContext()

  // Local state for action dialogs
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [renewalDrawerOpen, setRenewalDrawerOpen] = useState(false)
  const [finalDepartureConfirmOpen, setFinalDepartureConfirmOpen] = useState(false)
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false)
  const [checkOutConfirmOpen, setCheckOutConfirmOpen] = useState(false)
  const [createLeaseOpen, setCreateLeaseOpen] = useState(false)
  const [createStayOpen, setCreateStayOpen] = useState(false)
  const [editTransientOpen, setEditTransientOpen] = useState(false)
  const [cancelTransientOpen, setCancelTransientOpen] = useState(false)
  const [noShowTransientOpen, setNoShowTransientOpen] = useState(false)
  const [checkOutTransientOpen, setCheckOutTransientOpen] = useState(false)
  const [checkInConfirmOpen, setCheckInConfirmOpen] = useState(false)
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)

  const finalDeparture = useFinalDepartureWithLeaseComplete()
  const checkInMutation = useCheckInEvent()
  const checkOutEvent = useCheckOutEvent()
  const terminateMutation = useTerminateLease()
  const cancelTransientMutation = useCancelTransientStay()
  const checkOutTransientMutation = useCheckOutTransientStay()
  const noShowTransientMutation = useNoShowTransientStay()

  // Build prefill data from current allocation for "Create Lease" action
  const createLeasePrefill = useMemo((): LeasePrefillData => {
    const prefill: LeasePrefillData = {}

    // Pre-fill unit from current placement
    if (data.allocation.unitId) {
      prefill.unit_id = data.allocation.unitId
    }

    // Pre-fill client from aircraft owner
    if (data.aircraft.owner?.id) {
      prefill.client_id = data.aircraft.owner.id

      // Pre-fill primary contact if available
      const primaryContact = data.ownerContacts?.find((c) => c.isPrimaryContact)
      if (primaryContact) {
        prefill.contact_id = primaryContact.id
      }
    }

    // Pre-fill aircraft
    prefill.aircraft = [
      {
        id: data.aircraft.id,
        tailNumber: data.aircraft.tailNumber,
        typeName: data.aircraft.model
          ? `${data.aircraft.manufacturer ?? ''} ${data.aircraft.model}`.trim()
          : null,
        isPrimary: true,
      },
    ]

    return prefill
  }, [data])

  // Check if a departure schedule already exists (suppresses redundant departing_today row)
  const { data: upcomingSchedules } = useUpcomingAircraftSchedules(data.aircraft.id)
  const hasDepartureSchedule = (upcomingSchedules ?? []).length > 0

  const rows = useMemo(
    () => computeStatusRows(data, { hasDepartureSchedule }),
    [data, hasDepartureSchedule]
  )

  const displayRows = filterSeverities
    ? rows.filter((r) => filterSeverities.includes(r.severity))
    : rows
  const visible = displayRows.slice(0, MAX_VISIBLE)
  const remaining = displayRows.length - MAX_VISIBLE

  const renewalLeaseId = data.leaseContract?.id

  const confirmFinalDeparture = () => {
    if (!locationId || !data.leaseContract) return
    finalDeparture.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        lease_id: data.leaseContract.id,
        notes: 'Check out — lease completed via status row action',
      },
      {
        onSuccess: () => {
          setFinalDepartureConfirmOpen(false)
          toast.success(`${data.aircraft.tailNumber} checked out — lease completed`)
          closeCard()
        },
        onError: (err) => {
          toast.error(`Check out failed: ${err.message}`)
        },
      }
    )
  }

  const handleCheckIn = () => {
    if (!locationId) return
    checkInMutation.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        transient_stay_id: data.allocation.transientStayId ?? undefined,
        notes: 'Check in via status row action',
      },
      {
        onSuccess: () => {
          setCheckInConfirmOpen(false)
          toast.success(`${data.aircraft.tailNumber} checked in`)
        },
        onError: (err) => {
          toast.error(`Check in failed: ${err.message}`)
        },
      }
    )
  }

  const confirmCheckOut = () => {
    if (!locationId) return
    checkOutEvent.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        transient_stay_id: data.allocation.transientStayId ?? undefined,
        notes: 'Check out — allocation completed via status row action',
      },
      {
        onSuccess: () => {
          setCheckOutConfirmOpen(false)
          toast.success(`${data.aircraft.tailNumber} checked out`)
          closeCard()
        },
        onError: (err) => {
          toast.error(`Check out failed: ${err.message}`)
        },
      }
    )
  }

  /** Resolve row click target to a store action */
  function handleRowClick(row: StatusRowDef) {
    if (!row.rowClickTarget) return
    if (row.rowClickTarget === 'lease-details' && onLeaseDetailsClick) {
      onLeaseDetailsClick()
    } else if (row.rowClickTarget === 'transient-details' && onTransientDetailsClick) {
      onTransientDetailsClick()
    } else if (row.rowClickTarget === 'financial' || row.rowClickTarget === 'fuel' || row.rowClickTarget === 'schedule') {
      openFullModal(row.rowClickTarget as 'financial' | 'fuel' | 'schedule')
    } else if (row.rowClickTarget === 'aircraft-details') {
      openEditForm('aircraft-details')
    }
  }

  /** Resolve primary action type to a callback */
  function handleAction(row: StatusRowDef) {
    switch (row.actionType) {
      case 'renew_lease':
        setRenewalDrawerOpen(true)
        break
      case 'edit_insurance':
        openEditForm('aircraft-details', 'insuranceExpiry')
        break
      case 'checkout_allocation':
        setCheckOutConfirmOpen(true)
        break
      case 'checkout_transient':
        setCheckOutTransientOpen(true)
        break
      case 'checkin_transient':
        setCheckInConfirmOpen(true)
        break
      case 'no_show_transient':
        setNoShowTransientOpen(true)
        break
      case 'mark_returned':
        setArrivalDialogOpen(true)
        break
      case 'log_fuel':
        openEditForm('fuel-record')
        break
      case 'add_departure_schedule':
        setScheduleDialogOpen(true)
        break
    }
  }

  /** Resolve dropdown action key to a callback */
  function handleDropdownAction(actionKey: string) {
    switch (actionKey) {
      case 'edit_lease':
        setEditDrawerOpen(true)
        break
      case 'checkout_allocation':
        setCheckOutConfirmOpen(true)
        break
      case 'renew_lease':
        setRenewalDrawerOpen(true)
        break
      case 'terminate_lease':
        setTerminateDialogOpen(true)
        break
      case 'edit_transient':
        setEditTransientOpen(true)
        break
      case 'cancel_transient':
        setCancelTransientOpen(true)
        break
      case 'no_show_transient':
        setNoShowTransientOpen(true)
        break
      case 'checkout_transient':
        setCheckOutTransientOpen(true)
        break
      case 'checkin_transient':
        setCheckInConfirmOpen(true)
        break
      case 'view_transient':
        if (onTransientDetailsClick) onTransientDetailsClick()
        break
      case 'view_lease':
        if (onLeaseDetailsClick) onLeaseDetailsClick()
        break
      case 'view_schedule':
        openFullModal('schedule')
        break
      case 'extend_stay':
        setEditTransientOpen(true)
        break
      case 'create_lease':
        setCreateLeaseOpen(true)
        break
      case 'create_transient':
        setCreateStayOpen(true)
        break
      case 'complete_setup':
        triggerCompleteSetup()
        break
      case 'add_departure_schedule':
        setScheduleDialogOpen(true)
        break
    }
  }

  /** Build resolved dropdown items for a row */
  /** Resolve multi-action buttons for a row */
  function triggerCompleteSetup() {
    const typeName = data.aircraft.model
      ? `${data.aircraft.manufacturer ?? ''} ${data.aircraft.model}`.trim()
      : null
    const fuelType = data.aircraft.defaultFuelType
      ?? inferFuelTypeFromCategory(data.aircraft.category)
    openCompleteSetup({
      allocationId: data.allocationId,
      aircraftId: data.aircraft.id,
      tailNumber: data.aircraft.tailNumber,
      typeName,
      defaultFuelType: fuelType,
    })
  }

  function buildActionButtons(row: StatusRowDef): { label: string; onClick: () => void }[] | undefined {
    if (!row.actions?.length) return undefined
    return row.actions.map((a) => ({
      label: a.label,
      onClick: () => {
        if (a.key === 'complete_setup') triggerCompleteSetup()
        else if (a.key === 'create_lease') setCreateLeaseOpen(true)
        else if (a.key === 'create_transient') setCreateStayOpen(true)
      },
    }))
  }

  function buildDropdownItems(row: StatusRowDef): StatusRowDropdownItem[] | undefined {
    if (!row.dropdownActions?.length) return undefined
    return row.dropdownActions.map((da) => ({
      label: da.label,
      onClick: () => handleDropdownAction(da.actionKey),
      destructive: da.destructive,
    }))
  }

  if (displayRows.length === 0) return null

  return (
    <>
      <div>
        {visible.map((row, i) => (
          <StatusRow
            key={row.key}
            severity={row.severity}
            iconName={row.iconName}
            textIconName={row.textIconName}
            text={row.text}
            progressValue={row.progressValue}
            tooltipLines={row.tooltipLines}
            actionLabel={row.actionType ? ACTION_LABELS[row.actionType] : undefined}
            onAction={row.actionType ? () => handleAction(row) : undefined}
            actionButtons={buildActionButtons(row)}
            dropdownItems={buildDropdownItems(row)}
            onRowClick={row.rowClickTarget ? () => handleRowClick(row) : undefined}
            isLast={i === visible.length - 1 && remaining <= 0}
          />
        ))}
        {remaining > 0 && (
          <div
            className="text-[10px] text-muted-foreground pl-3 py-0.5 cursor-pointer hover:underline bg-muted/30"
            onClick={() => openFullModal('aircraft')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openFullModal('aircraft') }}
          >
            +{remaining} more
          </div>
        )}
      </div>

      {/* Edit Lease Drawer */}
      {data.leaseContract && (
        <LeaseMutateDrawer
          open={editDrawerOpen}
          onOpenChange={setEditDrawerOpen}
          leaseId={data.leaseContract.id}
          lockedLocationId={locationId ?? undefined}
        />
      )}

      {/* Renewal Drawer */}
      {renewalLeaseId && (
        <LeaseMutateDrawer
          open={renewalDrawerOpen}
          onOpenChange={setRenewalDrawerOpen}
          renewalLeaseId={renewalLeaseId}
          lockedLocationId={locationId ?? undefined}
        />
      )}

      {/* Check Out with Lease Complete Confirmation */}
      <AlertDialog
        open={finalDepartureConfirmOpen}
        onOpenChange={setFinalDepartureConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Check Out — {data.aircraft.tailNumber}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {data.aircraft.tailNumber} as permanently departed
              and complete the lease contract
              {data.leaseContract?.contractNumber
                ? ` (${data.leaseContract.contractNumber})`
                : ''}
              . The aircraft will be removed from the ramp view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFinalDeparture}
              disabled={finalDeparture.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {finalDeparture.isPending
                ? 'Processing...'
                : 'Confirm Check Out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check Out Confirmation */}
      <AlertDialog
        open={checkOutConfirmOpen}
        onOpenChange={setCheckOutConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Check Out — {data.aircraft.tailNumber}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {data.aircraft.tailNumber} as permanently departed.
              The aircraft will be removed from the ramp view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCheckOut}
              disabled={checkOutEvent.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {checkOutEvent.isPending ? 'Processing...' : 'Confirm Check Out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check-In Confirmation (transient booking) */}
      <AlertDialog
        open={checkInConfirmOpen}
        onOpenChange={setCheckInConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Check In — {data.aircraft.tailNumber}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will activate the allocation and mark {data.aircraft.tailNumber} as present on the ramp.
              {data.transientStay?.bookingNumber
                ? ` Booking ${data.transientStay.bookingNumber} will be updated to checked-in.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCheckIn}
              disabled={checkInMutation.isPending}
            >
              {checkInMutation.isPending ? 'Processing...' : 'Confirm Check In'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Arrival Dialog (mark returned from away) */}
      {locationId && (
        <ArrivalDialog
          open={arrivalDialogOpen}
          onOpenChange={setArrivalDialogOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Schedule Edit Dialog (create departure schedule) */}
      <ScheduleEditDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        aircraftId={data.aircraft.id}
        locationId={locationId ?? undefined}
      />

      {/* Create Lease Drawer */}
      <LeaseMutateDrawer
        open={createLeaseOpen}
        onOpenChange={setCreateLeaseOpen}
        lockedLocationId={locationId ?? undefined}
        prefillData={createLeasePrefill}
      />

      {/* Create Transient Stay Drawer */}
      <TransientStayMutateDrawer
        open={createStayOpen}
        onOpenChange={setCreateStayOpen}
      />

      {/* Terminate Lease Dialog */}
      {data.leaseContract && (
        <TerminateLeaseDialog
          open={terminateDialogOpen}
          onOpenChange={setTerminateDialogOpen}
          contractNumber={data.leaseContract.contractNumber}
          onConfirm={(params) => {
            if (!data.leaseContract) return
            terminateMutation.mutate(
              { id: data.leaseContract.id, ...params },
              {
                onSuccess: () => {
                  setTerminateDialogOpen(false)
                  toast.success(`Lease ${data.leaseContract!.contractNumber} terminated`)
                },
              }
            )
          }}
          isPending={terminateMutation.isPending}
        />
      )}

      {/* Edit Transient Stay Drawer */}
      {data.transientStay && (
        <TransientStayMutateDrawer
          open={editTransientOpen}
          onOpenChange={setEditTransientOpen}
          transientStayId={data.transientStay.id}
        />
      )}

      {/* Cancel Transient Stay Dialog */}
      {data.transientStay && (
        <CancelTransientStayDialog
          open={cancelTransientOpen}
          onOpenChange={setCancelTransientOpen}
          bookingNumber={data.transientStay.bookingNumber ?? ''}
          onConfirm={(params) => {
            cancelTransientMutation.mutate(
              { id: data.transientStay!.id, ...params },
              {
                onSuccess: () => {
                  setCancelTransientOpen(false)
                  toast.success(`Booking ${data.transientStay!.bookingNumber} cancelled`)
                },
              }
            )
          }}
          isPending={cancelTransientMutation.isPending}
        />
      )}

      {/* No-Show Transient Stay Dialog */}
      {data.transientStay && (
        <NoShowTransientStayDialog
          open={noShowTransientOpen}
          onOpenChange={setNoShowTransientOpen}
          bookingNumber={data.transientStay.bookingNumber ?? ''}
          onConfirm={() => {
            noShowTransientMutation.mutate(
              data.transientStay!.id,
              {
                onSuccess: () => {
                  setNoShowTransientOpen(false)
                  toast.success(`Booking ${data.transientStay!.bookingNumber} marked as no-show`)
                },
              }
            )
          }}
          isPending={noShowTransientMutation.isPending}
        />
      )}

      {/* Check Out Transient Stay Dialog */}
      {data.transientStay && (
        <CheckOutTransientStayDialog
          open={checkOutTransientOpen}
          onOpenChange={setCheckOutTransientOpen}
          bookingNumber={data.transientStay.bookingNumber ?? ''}
          onConfirm={() => {
            checkOutTransientMutation.mutate(
              data.transientStay!.id,
              {
                onSuccess: () => {
                  setCheckOutTransientOpen(false)
                  toast.success(`Booking ${data.transientStay!.bookingNumber} checked out`)
                },
              }
            )
          }}
          isPending={checkOutTransientMutation.isPending}
        />
      )}
    </>
  )
}
